import {
  Button,
  ClickAwayListener,
  darken,
  lighten,
  Paper,
  Popper as MuiPopper,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { RefObject } from 'react';
import Translate from '@/component/Translate';
import { Rating } from '@/feature/chatbot/api.types';
import { ChatMessage } from '@/feature/chatbot/types';
import { useTexts } from '@/feature/lang';
import { Icon } from '@/icon/Icon';
import { useRating } from './useRating';

interface RatingPopperProps {
  message: ChatMessage;
  anchorRef: RefObject<HTMLButtonElement | null>;
  isOpen: boolean;
  close: () => void;
}

const Popper = styled(MuiPopper, { name: 'Chatbot', slot: 'RatingPopper' })(
  ({ theme }) => ({
    zIndex: theme.zIndex.modal,
  })
);

const RateButton = styled(Button, {
  name: 'Chatbot',
  slot: 'RateButton',
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ selected, theme: { palette } }) => ({
  ...(selected && {
    background: palette.background.inverted,
    color: palette.getContrastText(palette.background.inverted),
    '&:hover, &:focus': {
      backgroundColor:
        palette.mode === 'light'
          ? lighten(palette.background.inverted, palette.action.focusOpacity)
          : darken(palette.background.inverted, palette.action.focusOpacity),
    },
  }),
}));

const ButtonContainer = styled('div', {
  name: 'Chatbot',
  slot: 'RateButtonContainer',
})(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const RatingPaper = styled(Paper, {
  name: 'Chatbot',
  slot: 'RatingPaper',
})(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  boxShadow: theme.shadows[8],
}));

export const RatingPopper = ({
  message,
  anchorRef,
  isOpen,
  close,
}: RatingPopperProps) => {
  const texts = useTexts().component.chatbot;
  const { rating, castVote, options } = useRating(message);

  const VirtualAnchor = {
    getBoundingClientRect: () =>
      anchorRef.current
        ? anchorRef.current.getBoundingClientRect()
        : ({} as DOMRect),
  };

  const handleVote = (rating?: Rating) => {
    castVote(rating);
    close();
  };

  return (
    <ClickAwayListener onClickAway={close}>
      <Popper open={isOpen} placement={'bottom-end'} anchorEl={VirtualAnchor}>
        <RatingPaper elevation={8}>
          <Typography variant="h4">
            <Translate text={texts.rating.question} />
          </Typography>
          <ButtonContainer>
            {options.map((r, i) => {
              const selected = rating === r.rating;
              return (
                <RateButton
                  key={i}
                  selected={selected}
                  onClick={() => handleVote(selected ? undefined : r.rating)}
                  startIcon={<Icon name={r.icon} size="small" />}
                  variant="contained"
                  color="grey"
                >
                  {r.text}
                </RateButton>
              );
            })}
          </ButtonContainer>
        </RatingPaper>
      </Popper>
    </ClickAwayListener>
  );
};
